import type { KeyboardEvent } from 'react';
import { useNavigate } from 'react-router';
import { twMerge } from 'tailwind-merge';
import MapIdentifyIcon from '../../assets/svg/map-identify.svg?react';

export type SidebarVerificationStatus =
  | 'NotStarted'
  | 'InProgress'
  | 'UnderReview'
  | 'Approved'
  | 'Rejected'
  | 'RequiresAdditionalInfo'
  | 'Loading';

export interface SidebarVerificationCard {
  status: SidebarVerificationStatus;
  title: string;
  badgeText: string;
  linkTo?: string;
  isActionable: boolean;
  show: boolean;
}

export interface KybStatusSectionProps {
  kybStatus: SidebarVerificationCard;
  isActiveRoute: (path: string) => boolean;
}

interface StatusStyle {
  container: string;
  title: string;
  badge: string;
  iconWrapper: string;
  icon?: string;
  hover?: string;
  closeIcon?: string;
}

const STATUS_STYLES: Record<SidebarVerificationStatus, StatusStyle> = {
  NotStarted: {
    container: 'bg-[#F48A39] text-[#1F140C]',
    title: 'text-[#1F140C]',
    badge: 'bg-[#1F1915] text-[#FB923C]',
    iconWrapper: 'bg-[#FDE7CF] text-[#1F140C]',
    icon: 'text-[#1F140C]',
    hover: 'hover:bg-[#E17825]',
  },
  InProgress: {
    container: 'bg-[#101315] text-white',
    title: 'text-white',
    badge: 'bg-[#1F2F57] text-[#A8C7FF]',
    iconWrapper: 'bg-white/10 text-white',
    icon: 'text-[#FACC15]',
    hover: 'hover:bg-[#15191C]',
  },
  UnderReview: {
    container: 'bg-[#E7F3FF] text-[#0E203F]',
    title: 'text-[#0B1F3B]',
    badge: 'bg-[#3B82F6] text-white',
    iconWrapper: 'bg-white text-[#0B1F3B]',
    icon: 'text-[#0B1F3B]',
  },
  Approved: {
    container: 'bg-[#101214] text-white',
    title: 'text-white',
    badge: 'bg-[#34D399] text-[#052716]',
    iconWrapper: 'bg-white/10 text-white',
    icon: 'text-[#34D399]',
    closeIcon: 'text-white/60',
  },
  Rejected: {
    container: 'bg-[#2C1515] text-[#FECACA]',
    title: 'text-[#FFD1D1]',
    badge: 'bg-[#F87171] text-[#420A0A]',
    iconWrapper: 'bg-[#4A1E1E] text-[#FECACA]',
    icon: 'text-[#FECACA]',
    hover: 'hover:bg-[#341818]',
  },
  RequiresAdditionalInfo: {
    container: 'bg-[#2B1F0F] text-[#FDE68A]',
    title: 'text-[#FDE68A]',
    badge: 'bg-[#FCD34D] text-[#422006]',
    iconWrapper: 'bg-[#3C2A15] text-[#FDE68A]',
    icon: 'text-[#FACC15]',
    hover: 'hover:bg-[#382513]',
  },
  Loading: {
    container: 'bg-[#151717] text-white',
    title: 'text-white',
    badge: 'bg-[#232626] text-white',
    iconWrapper: 'bg-white/10 text-white',
    icon: 'text-white',
  },
};

const baseContainerClasses =
  'relative w-60 h-16 rounded-xl px-2.5 py-2.5 flex flex-col justify-between gap-1.5 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40';

const badgeClasses =
  'inline-flex self-start items-center rounded-md px-2 py-1 text-[11px] font-medium leading-none';

export const KybStatusSection = ({
  kybStatus,
  isActiveRoute,
}: KybStatusSectionProps) => {
  const navigate = useNavigate();

  if (!kybStatus.show) {
    return null;
  }

  const style = STATUS_STYLES[kybStatus.status] ?? STATUS_STYLES.Loading;
  const isActive = kybStatus.linkTo ? isActiveRoute(kybStatus.linkTo) : false;
  const isInteractive = kybStatus.isActionable && Boolean(kybStatus.linkTo);

  const navigateToKybLink = () => {
    if (isInteractive && kybStatus.linkTo) {
      navigate(kybStatus.linkTo);
    }
  };

  const activateOnEnterOrSpace = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractive) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      navigateToKybLink();
    }
  };

  return (
    <div
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : -1}
      onClick={navigateToKybLink}
      onKeyDown={activateOnEnterOrSpace}
      className={twMerge(
        baseContainerClasses,
        style.container,
        isInteractive ? 'cursor-pointer' : 'cursor-default',
        isInteractive && style.hover,
        isActive && 'brightness-105',
      )}
    >
      <div className="flex items-center gap-2">
        <div
          className={twMerge(
            'flex h-6 w-6 items-center justify-center rounded-full',
            style.iconWrapper,
          )}
        >
          <MapIdentifyIcon
            className={twMerge(
              'h-3.5 w-3.5 text-inherit [&_path]:fill-current',
              style.icon,
            )}
            aria-hidden
          />
        </div>
        <div className="flex-1 text-left">
          <p
            className={twMerge(
              'text-sm font-medium leading-tight',
              style.title,
            )}
          >
            {kybStatus.title}
          </p>
        </div>
        {style.closeIcon ? (
          <span
            aria-hidden
            className={twMerge(
              'text-xs font-semibold leading-none self-start -mt-0.5',
              style.closeIcon,
            )}
          >
            ×
          </span>
        ) : null}
      </div>

      {kybStatus.badgeText ? (
        <span className={twMerge(badgeClasses, style.badge)}>
          {kybStatus.badgeText}
        </span>
      ) : null}
    </div>
  );
};
